import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';

const MetadataModal = ({ show, onHide, metadataFields, onSave, initialMetadata = {} }) => {
  const [selectedField, setSelectedField] = useState('');
  const [fieldValue, setFieldValue] = useState('');
  const [metadata, setMetadata] = useState(initialMetadata);
  const [error, setError] = useState('');

  useEffect(() => {
    setMetadata(initialMetadata);
  }, [initialMetadata]);

  const handleAddField = () => {
    if (!selectedField || !fieldValue.trim()) {
      setError('Please select a field and enter a value');
      return;
    }

    const field = metadataFields.find(f => f.fieldKey === selectedField);
    if (!field) {
      setError('Invalid field selected');
      return;
    }

    setMetadata(prev => ({
      ...prev,
      [selectedField]: fieldValue
    }));

    setSelectedField('');
    setFieldValue('');
    setError('');
  };

  const handleRemoveField = (fieldKey) => {
    setMetadata(prev => {
      const updated = { ...prev };
      delete updated[fieldKey];
      return updated;
    });
  };

  const handleDone = () => {
    onSave(metadata);
    onHide();
  };

  const getInputType = (dataType) => {
    switch (dataType) {
      case 'number': return 'number';
      case 'date': return 'date';
      case 'boolean': return 'select';
      default: return 'text';
    }
  };

  const selectedFieldData = metadataFields.find(f => f.fieldKey === selectedField);

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Metadata Fields</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Label>Select Field</Form.Label>
            <Form.Select
              value={selectedField}
              onChange={(e) => {
                setSelectedField(e.target.value);
                setFieldValue('');
                setError('');
              }}
            >
              <option value="">Choose field...</option>
              {metadataFields
                .filter(field => !metadata[field.fieldKey])
                .map(field => (
                  <option key={field.fieldKey} value={field.fieldKey}>
                    {field.name}
                  </option>
                ))}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label>Value</Form.Label>
            {selectedFieldData?.dataType === 'boolean' ? (
              <Form.Select
                value={fieldValue}
                onChange={(e) => setFieldValue(e.target.value)}
              >
                <option value="">Select...</option>
                <option value="true">True</option>
                <option value="false">False</option>
              </Form.Select>
            ) : (
              <Form.Control
                type={getInputType(selectedFieldData?.dataType)}
                value={fieldValue}
                onChange={(e) => setFieldValue(e.target.value)}
                placeholder={`Enter ${selectedFieldData?.name || 'value'}`}
              />
            )}
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Button
              variant="primary"
              onClick={handleAddField}
              disabled={!selectedField || !fieldValue.trim()}
            >
              Add
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-3">
          <Form.Label>Current Metadata (JSON)</Form.Label>
          <Form.Control
            as="textarea"
            rows={8}
            value={JSON.stringify(metadata, null, 2)}
            readOnly
            className="bg-light"
          />
        </Form.Group>

        {Object.keys(metadata).length > 0 && (
          <div className="mb-3">
            <h6>Added Fields:</h6>
            {Object.entries(metadata).map(([key, value]) => {
              const field = metadataFields.find(f => f.fieldKey === key);
              return (
                <div key={key} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                  <span><strong>{field?.name || key}:</strong> {value}</span>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveField(key)}
                  >
                    Remove
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MetadataModal;